<template>
  <div class="dashboard">
    <!-- Hero Section com gradiente e stats em tempo real -->
    <v-card class="hero-section mb-6" elevation="0">
      <div class="hero-gradient">
        <v-row align="center" class="pa-6">
          <v-col cols="12" md="6">
            <div class="d-flex align-center mb-2">
              <v-icon size="40" color="white" class="mr-3">mdi-cube-scan</v-icon>
              <div>
                <h1 class="text-h3 font-weight-bold text-white">QuarnX Explorer</h1>
                <div class="d-flex align-center" style="gap: 8px;">
                  <p class="text-subtitle-1 text-white mb-0" style="opacity: 0.85">{{ t('app.subtitle') }}</p>
                  <v-chip size="x-small" color="green-accent-3" variant="flat" label class="font-weight-bold">
                    {{ blockchainStore.stats.networkName || 'DevNet' }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="d-flex justify-end flex-wrap" style="gap: 16px;">
              <div class="hero-stat">
                <div class="hero-stat-value">{{ blockchainStore.getLatestBlockNumber.toLocaleString() }}</div>
                <div class="hero-stat-label">{{ t('blocks.title') }}</div>
                <div class="hero-pulse"></div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-value">{{ blockchainStore.stats.totalTransactions.toLocaleString() }}</div>
                <div class="hero-stat-label">{{ t('tx.title') }}</div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-value">{{ blockchainStore.getActiveValidatorsCount }}</div>
                <div class="hero-stat-label">{{ t('validators.title') }}</div>
              </div>
              <div class="hero-stat">
                <div class="hero-stat-value">{{ blockchainStore.getAverageBlockTimeFormatted }}</div>
                <div class="hero-stat-label">{{ t('network.blockTime') }}</div>
              </div>
              <div class="hero-stat" v-if="blockchainStore.stats.totalSupply > 0">
                <div class="hero-stat-value">{{ formatSupply(blockchainStore.stats.totalSupply) }}</div>
                <div class="hero-stat-label">{{ t('supply.total') }}</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card>
    
    <!-- Busca Rápida Unificada -->
    <v-card class="mb-6" elevation="2">
      <v-card-text class="pa-4">
        <v-text-field
          v-model="buscaUnificada"
          :label="t('app.search')"
          variant="outlined"
          density="comfortable"
          hide-details
          prepend-inner-icon="mdi-magnify"
          clearable
          @keydown.enter="buscarUnificado"
          class="busca-field"
        >
          <template v-slot:append>
            <v-btn color="primary" variant="flat" @click="buscarUnificado" class="ml-2">
              {{ t('app.search').split(' ')[0] }}
            </v-btn>
          </template>
        </v-text-field>
      </v-card-text>
    </v-card>
    
    <!-- Blocos Recentes e Transações -->
    <v-row>
      <v-col cols="12" lg="6">
        <BlocosList
          :titulo="t('dashboard.latestBlocks')"
          :blocos="blockchainStore.latestBlocks"
          :carregando="blockchainStore.isLoadingBlocks"
          :mostrar-colunas="['number', 'timestamp', 'transactionCount', 'validator', 'actions']"
          :items-por-pagina="5"
          esconder-paginacao
        />
      </v-col>
      
      <v-col cols="12" lg="6">
        <TransacoesList
          :titulo="t('dashboard.latestTx')"
          :transacoes="blockchainStore.latestTransactions"
          :carregando="blockchainStore.isLoadingTransactions"
          :mostrar-colunas="['hash', 'from', 'direction', 'to', 'value', 'status']"
          :items-por-pagina="5"
          esconder-paginacao
        />
      </v-col>
    </v-row>
    
    <!-- Módulos do Sistema -->
    <h2 class="text-h5 mt-8 mb-4">{{ t('dashboard.title') }}</h2>
    
    <v-row>
      <v-col cols="12" md="4">
        <v-card
          to="/developer"
          class="module-card"
          height="200"
          hover
        >
          <v-card-item>
            <v-card-title>
              <v-icon color="primary" size="large" class="mb-2">mdi-code-braces</v-icon>
              <div class="text-h5">{{ t('modules.devEnv') }}</div>
            </v-card-title>
            <v-card-text>
              {{ t('modules.devEnvDesc') }}
            </v-card-text>
          </v-card-item>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="4">
        <v-card
          to="/dex"
          class="module-card"
          height="200"
          hover
        >
          <v-card-item>
            <v-card-title>
              <v-icon color="primary" size="large" class="mb-2">mdi-chart-line</v-icon>
              <div class="text-h5">{{ t('dex.title') }}</div>
            </v-card-title>
            <v-card-text>
              {{ t('modules.dexDesc') }}
            </v-card-text>
          </v-card-item>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="4">
        <v-card
          to="/validators"
          class="module-card"
          height="200"
          hover
        >
          <v-card-item>
            <v-card-title>
              <v-icon color="primary" size="large" class="mb-2">mdi-view-dashboard-outline</v-icon>
              <div class="text-h5">{{ t('modules.advancedViews') }}</div>
            </v-card-title>
            <v-card-text>
              {{ t('modules.advancedViewsDesc') }}
            </v-card-text>
          </v-card-item>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Resumo do PoSEM -->
    <v-card class="mt-8">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
        {{ t('posem.title') }}
      </v-card-title>
      
      <v-card-text>
        <p>O QuarnX utiliza um mecanismo de consenso inovador chamado PoSEM (Proof of Stake and Ecosystem Merit), que combina os benefícios do Proof of Stake tradicional com um sistema de mérito baseado na contribuição para o ecossistema.</p>
        
        <v-alert
          color="primary"
          icon="mdi-information-outline"
          variant="outlined"
          class="mt-4"
          density="comfortable"
        >
          <strong>Características principais do PoSEM:</strong>
          <ul class="mt-2">
            <li>Seleção de validadores baseada em stake e mérito no ecossistema</li>
            <li>Eficiência energética muito superior a Proof of Work</li>
            <li>Recompensas proporcionais à contribuição para a rede</li>
            <li>Maior resistência a ataques de conluio</li>
            <li>Incentivo à participação ativa na rede</li>
          </ul>
        </v-alert>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="mostrarMaisInfo = !mostrarMaisInfo"
        >
          {{ mostrarMaisInfo ? t('posem.lessInfo') : t('posem.moreInfo') }}
          <v-icon>{{ mostrarMaisInfo ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </v-card-actions>
      
      <v-expand-transition>
        <div v-if="mostrarMaisInfo">
          <v-divider></v-divider>
          <v-card-text>
            <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ t('posem.howItWorks') }}</h3>
            <p class="mb-3">A cada slot de tempo, um validador é escolhido para propor um novo bloco. A probabilidade de seleção de um validador é determinada por dois fatores principais:</p>
            
            <v-row>
              <v-col cols="12" md="6">
                <v-card variant="outlined">
                  <v-card-title class="text-subtitle-1">
                    <v-icon color="primary" class="mr-2">mdi-currency-usd</v-icon>
                    Stake (Peso: 70%)
                  </v-card-title>
                  <v-card-text>
                    Quantidade de tokens QuarnX alocados como garantia pelo validador. Quanto maior o stake, maior a probabilidade de seleção, seguindo uma distribuição proporcional.
                  </v-card-text>
                </v-card>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-card variant="outlined">
                  <v-card-title class="text-subtitle-1">
                    <v-icon color="primary" class="mr-2">mdi-medal-outline</v-icon>
                    Mérito (Peso: 30%)
                  </v-card-title>
                  <v-card-text>
                    Pontuação baseada na contribuição para o ecossistema, incluindo tempo online, disponibilidade, velocidade de propagação e participação no desenvolvimento.
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            
            <p class="mt-4">Os validadores são incentivados a manter alta disponibilidade e contribuir ativamente para o desenvolvimento do ecossistema, pois isso aumenta sua pontuação de mérito e, consequentemente, suas chances de receber recompensas de validação.</p>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import StatusCard from '@/components/common/StatusCard.vue'
import NetworkStatus from '@/components/common/NetworkStatus.vue'
import BlocosList from '@/components/blocks/BlocosList.vue'
import TransacoesList from '@/components/transactions/TransacoesList.vue'

export default {
  name: 'DashboardView',
  components: {
    StatusCard,
    NetworkStatus,
    BlocosList,
    TransacoesList
  },
  data() {
    return {
      buscaUnificada: '',
      mostrarMaisInfo: false
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    }
  },
  methods: {
    buscarUnificado() {
      const query = (this.buscaUnificada || '').trim()
      if (!query) return
      
      // Detectar tipo automaticamente
      if (/^\d+$/.test(query)) {
        this.$router.push({ name: 'block-details', params: { id: query } })
      } else if (/^[0-9a-fA-F]{16,}$/.test(query)) {
        this.$router.push({ path: '/search', query: { q: query } })
      } else {
        this.$router.push({ path: '/search', query: { q: query } })
      }
    },

    formatSupply(nanoValue) {
      if (!nanoValue || nanoValue <= 0) return '0'
      const kinx = nanoValue / 1_000_000_000
      if (kinx >= 1_000_000) return `${(kinx / 1_000_000).toFixed(1)}M`
      if (kinx >= 1_000) return `${(kinx / 1_000).toFixed(1)}K`
      return kinx.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
    }
  },
  async mounted() {
    // Carrega dados iniciais
    await this.blockchainStore.initializeData()
  }
}
</script>

<style scoped>
.dashboard {
  padding-bottom: 32px;
}

.hero-section {
  border-radius: 16px;
  overflow: hidden;
}

.hero-gradient {
  background: linear-gradient(135deg, #6200EA 0%, #3700B3 40%, #1A237E 100%);
  position: relative;
  overflow: hidden;
}

.hero-gradient::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}

.hero-gradient::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
}

.hero-stat {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 12px 20px;
  text-align: center;
  min-width: 100px;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.hero-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.hero-stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.hero-pulse {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00E676;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(0, 230, 118, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 230, 118, 0); }
}

.busca-field :deep(.v-field) {
  border-radius: 12px;
}

.module-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 12px;
}

.module-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.15);
}
</style>
